import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} currentIndex - The current slide indicator.
 * @property {HTMLElement[]} slides - The slide elements.
 * @property {HTMLElement[]} mediaItems - The media background elements.
 * @property {HTMLVideoElement[]} videos - The video elements.
 * @property {HTMLSourceElement[]} sources - The video source elements.
 */

/**
 * Hero section component with slide navigation and background media transitions.
 *
 * @extends Component<Refs>
 */
class HeroComponent extends Component {
  /** @type {number} */
  currentIndex = 1;

  /** @type {number} */
  totalSlides = 0;

  /** @type {number | null} */
  #autoplayInterval = null;

  /** @type {boolean} */
  #autoplayEnabled = false;

  /** @type {number} */
  #autoplayDuration = 5000;

  connectedCallback() {
    super.connectedCallback();

    const slides = this.querySelectorAll('.hero-slider__slide');
    this.totalSlides = slides.length;

    // mouseenter ne bubble pas, impossible via délégation – on attache directement
    slides.forEach((slide) => {
      const el = /** @type {HTMLElement} */ (slide);
      const index = parseInt(el.dataset.index || '0', 10);
      el.addEventListener('mouseenter', () => {
        if (!this.#hoverQuery.matches) return;
        this.#activateSlide(index);
        if (this.#autoplayEnabled) this.#restartAutoplay();
      });
    });

    this.#autoplayEnabled = this.dataset.autoplay === 'true';
    this.#autoplayDuration = parseInt(this.dataset.autoplayDuration || '5000', 10);

    this.#loadActiveMedia(1);

    if (this.#autoplayEnabled && this.totalSlides > 1) {
      this.#startAutoplay();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopAutoplay();
  }

  /** @type {MediaQueryList} */
  #hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  /**
   * Select a slide by index via click (mobile only — ignoré sur desktop car on utilise hover)
   * @param {Event} event
   */
  selectSlide(event) {
    if (this.#hoverQuery.matches) return;

    const target = /** @type {HTMLElement} */ (event.target)?.closest('[data-index]');
    if (!target) return;

    const index = /** @type {HTMLElement} */ (target).dataset.index;
    if (!index) return;

    this.#activateSlide(parseInt(index, 10));

    if (this.#autoplayEnabled) {
      this.#restartAutoplay();
    }
  }

  /**
   * Go to previous slide
   */
  prevSlide() {
    let newIndex = this.currentIndex - 1;
    if (newIndex < 1) newIndex = this.totalSlides;
    this.#activateSlide(newIndex);

    if (this.#autoplayEnabled) {
      this.#restartAutoplay();
    }
  }

  /**
   * Go to next slide
   */
  nextSlide() {
    let newIndex = this.currentIndex + 1;
    if (newIndex > this.totalSlides) newIndex = 1;
    this.#activateSlide(newIndex);

    if (this.#autoplayEnabled) {
      this.#restartAutoplay();
    }
  }

  /**
   * Activate a slide by index with media transition
   * @param {number} index
   */
  #activateSlide(index) {
    if (index === this.currentIndex) return;

    const previousIndex = this.currentIndex;
    this.currentIndex = index;
    const indexStr = String(index);

    // Update slides
    this.querySelectorAll('.hero-slider__slide').forEach((slide) => {
      const el = /** @type {HTMLElement} */ (slide);
      el.classList.toggle('is-active', el.dataset.index === indexStr);
    });

    // Update content items (title + links)
    this.querySelectorAll('.hero-slider__content-item').forEach((content) => {
      const el = /** @type {HTMLElement} */ (content);
      el.classList.toggle('is-active', el.dataset.index === indexStr);
    });

    this.#transitionMedia(previousIndex, index);
    this.#updatePaginationIndicator();
  }

  /**
   * Transition between media items with fade effect
   * @param {number} fromIndex
   * @param {number} toIndex
   */
  #transitionMedia(fromIndex, toIndex) {
    const fromMedia = this.querySelector(`.hero-slider__media-item[data-index="${fromIndex}"]`);
    const toMedia = this.querySelector(`.hero-slider__media-item[data-index="${toIndex}"]`);

    if (fromMedia) {
      /** @type {HTMLElement} */ (fromMedia).classList.remove('is-active');
      this.#pauseMediaVideo(fromMedia);
    }

    if (toMedia) {
      this.#loadMediaIfNeeded(toMedia);
      /** @type {HTMLElement} */ (toMedia).classList.add('is-active');
      this.#playMediaVideo(toMedia);
    }
  }

  /**
   * Load active media on initial render
   * @param {number} index
   */
  #loadActiveMedia(index) {
    const media = this.querySelector(`.hero-slider__media-item[data-index="${index}"]`);
    if (media) {
      this.#loadMediaIfNeeded(media);
      this.#playMediaVideo(media);
    }
  }

  /**
   * Load video sources if not already loaded (deferred loading)
   * @param {Element} mediaItem
   */
  #loadMediaIfNeeded(mediaItem) {
    const videos = mediaItem.querySelectorAll('video');
    if (videos.length === 0) return;

    videos.forEach((video) => {
      const sources = video.querySelectorAll('source[data-src]');
      if (sources.length === 0) return;

      let needsLoad = false;
      sources.forEach((source) => {
        const dataSrc = source.getAttribute('data-src');
        if (dataSrc && !source.getAttribute('src')) {
          source.setAttribute('src', dataSrc);
          needsLoad = true;
        }
      });

      if (needsLoad) {
        video.load();
      }
    });
  }

  /**
   * Play video in media item
   * @param {Element} mediaItem
   */
  #playMediaVideo(mediaItem) {
    mediaItem.querySelectorAll('video').forEach((video) => {
      /** @type {HTMLVideoElement} */ (video).play().catch(() => { });
    });
  }

  /**
   * Pause video in media item
   * @param {Element} mediaItem
   */
  #pauseMediaVideo(mediaItem) {
    mediaItem.querySelectorAll('video').forEach((video) => {
      /** @type {HTMLVideoElement} */ (video).pause();
    });
  }

  /**
   * Update the pagination indicator
   */
  #updatePaginationIndicator() {
    const indicator = /** @type {HTMLElement | undefined} */ (this.refs.currentIndex);
    if (indicator) {
      indicator.textContent = String(this.currentIndex);
    }
  }

  /**
   * Start autoplay interval
   */
  #startAutoplay() {
    this.#stopAutoplay();
    this.#autoplayInterval = window.setInterval(() => {
      this.nextSlide();
    }, this.#autoplayDuration);
  }

  /**
   * Stop autoplay interval
   */
  #stopAutoplay() {
    if (this.#autoplayInterval !== null) {
      clearInterval(this.#autoplayInterval);
      this.#autoplayInterval = null;
    }
  }

  /**
   * Restart autoplay (reset timer)
   */
  #restartAutoplay() {
    this.#startAutoplay();
  }
}

customElements.define('hero-component', HeroComponent);
